// The Dynamic Allocator signal as the DA Signal and Market Pulse pages read it, from public/bi-data/daSignalData.json.
// No router or DOM here, so it can be unit-tested. Every figure comes from the file: a month's output is parsed
// from its own text ("Deploy 40%" -> 40, "Hold" -> 0); nothing is filled in. Figures keep the file's precision:
// "Deploy 12.5%" is shown as 12.5%, never 13%, and what is worked out from it (87.5% not deployed, −7.5 points) to
// the same places.
import { decimalsOf, roundTo, toPlaces } from "./sourcePrecision";
import { MONTH_SHORT } from "./asOf";
import { ist } from "../bossai/liveClock";

const MON = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
export { MONTH_SHORT };

// "Deploy 40%" -> 40, "Hold" -> 0 (no new money to equities), anything else -> null (shown as its own text).
const DEPLOY = /deploy\s+(\d+(?:\.(\d+))?)\s*%/i;
export const valueOf = (signal) => {
  const s = String(signal || "").trim();
  const d = DEPLOY.exec(s);
  if (d) return Number(d[1]);
  return /^hold\b/i.test(s) ? 0 : null;
};

// Decimal places the output is written with: "Deploy 12.5%" -> 1, "Deploy 40%" and "Hold" -> 0.
export const placesOf = (signal) => {
  const d = DEPLOY.exec(String(signal || ""));
  return d && d[2] ? Math.min(d[2].length, 4) : 0;
};
const dpOf = (r) => (r && typeof r.dp === "number" ? r.dp : decimalsOf(r && r.v));

// A month's share as the file writes it: "40%", "12.5%".
export const pctOf = (r) => (!r || r.v === null ? "" : `${toPlaces(r.v, dpOf(r))}%`);

// history rows of the file -> oldest first: { y, m, key, label: "Sep 2026", short: "Sep", signal, v, dp, context }.
// "April 2026" reads as "Apr 2026"; a row whose month cannot be read is left out.
export const parseRows = (history) => {
  const out = [];
  (Array.isArray(history) ? history : []).forEach((h) => {
    const m = /^([A-Za-z]+)\s+(\d{4})$/.exec(String((h && h.month) || "").trim());
    if (!m) return;
    const mi = MON.indexOf(m[1].slice(0, 3).toLowerCase());
    if (mi < 0) return;
    const signal = String(h.signal || "").trim();
    out.push({
      y: Number(m[2]),
      m: mi,
      key: Number(m[2]) * 12 + mi,
      label: `${MONTH_SHORT[mi]} ${m[2]}`,
      short: MONTH_SHORT[mi],
      signal,
      v: valueOf(signal),
      dp: placesOf(signal),
      context: String(h.context || "").trim(),
    });
  });
  return out.sort((a, b) => a.key - b.key);
};

// IST year and month of a moment (liveClock.ist).
const istMonth = (now) => {
  const t = ist(now);
  return { y: Number(t.iso.slice(0, 4)), m: t.mon };
};

// "This month" only when the data's month is the calendar month in India today.
export const isThisMonth = (row, now = Date.now()) => {
  if (!row) return false;
  const t = istMonth(now);
  return row.y === t.y && row.m === t.m;
};

// How a row reads in the chart readout and the table: Hold says what it means.
export const valueText = (r) => {
  if (!r) return "";
  return r.v === 0 ? `${r.signal} · 0% to equities` : r.signal;
};

// Short label for a bar or the chart's end: "40%", "Hold", or the signal text itself.
export const shortValue = (r) => (!r ? "" : r.v === null ? r.signal : r.v === 0 ? "Hold" : pctOf(r));

// Change in points between two months' outputs, with the formula it came from, to the places the two are written
// with (12.3 − 20 is −7.7 points).
export const deltaOf = (to, from) => {
  if (!to || !from || to.v === null || from.v === null) return null;
  const dp = Math.max(dpOf(to), dpOf(from));
  const d = roundTo(to.v - from.v, dp);
  return {
    d,
    text: d > 0 ? `+${toPlaces(d, dp)} points` : d < 0 ? `−${toPlaces(Math.abs(d), dp)} points` : "No change",
    tone: d > 0 ? "up" : d < 0 ? "down" : "flat",
    formula: `${pctOf(to)} − ${pctOf(from)}`,
  };
};

// The share not deployed, 100% less the output, to the output's places: { v: 87.5, dp: 1, text: "87.5%", formula }.
export const restOf = (r) => {
  if (!r || r.v === null) return null;
  const dp = dpOf(r);
  const v = roundTo(100 - r.v, dp);
  return { v, dp, text: `${toPlaces(v, dp)}%`, formula: `${toPlaces(v, dp)}% = 100% − ${pctOf(r)}` };
};

// One of the six checks. A figure that belongs to one month ("Current: 13.4", "At the Mar 2026 signal: 13.4")
// is dropped from the check's rule: that month's own note keeps it.
export const checkRule = (desc) =>
  String(desc || "")
    .replace(/\s*(?:Current|At the [A-Za-z]+ \d{4} signal)\s*:.*$/i, "")
    .trim();

export const checksOf = (signals) =>
  (Array.isArray(signals) ? signals : [])
    .filter((s) => s && s.title)
    .map((s, i) => ({ n: i + 1, title: String(s.title), plain: String(s.plain || "").trim(), rule: checkRule(s.desc) }));

// "Since your last visit": the browser keeps the latest month it showed ({ key, signal }). Newer months, or a new
// output for the same month, count as changes. A first visit (nothing stored) shows nothing.
export const changesSince = (seen, rows) => {
  const none = { count: 0, keys: [], base: null };
  if (!seen || typeof seen.key !== "number" || !rows || !rows.length) return none;
  const keys = rows.filter((r) => r.key > seen.key || (r.key === seen.key && r.signal !== seen.signal)).map((r) => r.key);
  if (!keys.length) return none;
  const label = `${MONTH_SHORT[seen.key % 12]} ${Math.floor(seen.key / 12)}`;
  const signal = String(seen.signal || "");
  return { count: keys.length, keys, base: { key: seen.key, label, signal, v: valueOf(signal), dp: placesOf(signal) } };
};

export const seenOf = (latest) => (latest ? { key: latest.key, signal: latest.signal } : null);

// The step chart's y axis (the chart itself is src/backtester_new/LineChart.js PlotChart, in DASignal.js).
export const Y_TICKS = [0, 20, 40, 60, 80, 100];
